import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import Sidebar from "../Components/Sidebar";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import {
  fetchAllowedMenus,
  fetchUserData,
  fetchUserEditData,
} from "../Services/APIService";
import { useNavigate, useLocation } from "react-router-dom";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";
import CreateContactModal from "./CreateUser";
import EditUserViewDemo from "../Components/EditUserDemo";
import { Toaster, toast } from "react-hot-toast";
import UserPasswordModal from "../Components/UserPasswordModal";
import OverlayLoader from "../Components/OverlayLoader";

interface User {
  tenantId: number;
  userId: number;
  userName: string;
  mobile: string;
  email: string;
  alternativePhone: string;
  reportTo: number;
  groupId: number;
  isFallbackUser: boolean;
  isActive: boolean;
  userStatusId: number;
  userTypeId: number;
  reportToUserName: string;
  groupName: string;
  userTypeName: string;
  loginUserName : string;
}

interface MenuItem {
  menuID: number; // Fixed typo from "menuID:ografi"
  menuName: string;
  menuUrl: string;
  icon: string;
  subMenuList?: [];
}

interface ContactFormData {
  email: string;
  firstName: string;
  lastName: string;
  contactOwner: string;
  jobTitle: string;
  phoneNumber: string;
}

const UserTable: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [allowedMenus, setAllowedMenus] = useState<MenuItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>("");
  const navigate = useNavigate();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<User[]>([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  const [editUserData, setEditUserData] = useState<any>(null);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  useEffect(() => {
    const getMenus = async () => {
      setIsLoading(true);
      try {
        const menuID = location.state?.menuID;
        const subMenuId = location.state?.subMenuId;
        const userDataUpdated = location.state?.userDataUpdated;

        const previousMenuID = localStorage.getItem("activeMenuID");
        if (menuID !== previousMenuID || menuID === null) {
          localStorage.setItem("activeMenuID", menuID);
        } else {
          localStorage.removeItem("activeMenuID");
        }

        if (userDataUpdated) {
          fetchUsers();
        }

        if (subMenuId == 3) {
          setIsModalOpen(true);
        }
      } catch (error) {
        console.error("Error fetching menus:", error);
      } finally {
        setIsLoading(false);
      }
    };

    getMenus();
  }, [location.state]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async (): Promise<void> => {
    try {
      const usersData = await fetchUserData();
      console.log("Fetched Users:", usersData);
      setUsers(usersData);
      setLoading(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
      setLoading(false);
    }
  };

  const handleEdit = async (userId: number, tenantId: number) => {
    try {
      const response = await fetchUserEditData(userId, tenantId);
      if (response.users && response.users.length > 0) {
        setEditUserData(response.users[0]);
        setIsEditModalOpen(true);
      } else {
        throw new Error("No user data found");
      }
    } catch (error) {
      console.error("Error fetching user edit data:", error);
      alert("Failed to fetch user data for editing");
    }
  };

  const handleRowSelected = (state: { selectedRows: User[] }) => {
    setSelectedRows(state.selectedRows);
  };

  const handleCreateContact = (contactData: ContactFormData) => {
    setIsModalOpen(false);
    fetchUsers(); // Refresh user data after creation
  };

  const handleEditSubmit = (updatedData: any) => {
    setIsEditModalOpen(false);
    setEditUserData(null);
    fetchUsers(); // Refresh user data after edit
  };

  const handleOpenPasswordModal = (userId: number) => {
    setSelectedUserId(userId);
    setIsPasswordModalOpen(true);
    fetchUsers();
  };

  const handleClosePasswordModal = () => {
    setIsPasswordModalOpen(false);
    setSelectedUserId(null);
    fetchUsers();
  };

  const filteredUsers = users.filter(
    (user) =>
      user.userName.toLowerCase().includes(searchText.toLowerCase()) ||
      user.email.toLowerCase().includes(searchText.toLowerCase()) ||
      user.mobile.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      name: "Name",
      selector: (row: User) => row.userName,
      sortable: true,
      cell: (row: User) => (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div
            style={{
              width: "24px",
              height: "24px",
              borderRadius: "50%",
              backgroundColor: "rgb(167 133 144)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "10px",
              fontWeight: "bold",
            }}
          >
            {row.userName.charAt(0).toUpperCase()}
          </div>
          <span style={{ fontWeight: "500", color: "#33475b" }}>
            {row.userName}
          </span>
        </div>
      ),
      width: "200px",
    },
    {
      name: "Email",
      selector: (row: User) => row.email,
      sortable: true,
    },
    { name: "Mobile", selector: (row: User) => row.mobile, sortable: true },
    {
      name: "User Status",
      selector: (row: User) => row.isActive,
      sortable: true,
      cell: (row: User) => {
        const isActive =
          typeof row.isActive === "boolean"
            ? row.isActive
            : row.isActive === "true";
        return (
          <span className={isActive ? "active-status" : "inactive-status"}>
            {isActive ? "ACTIVE" : "INACTIVE"}
          </span>
        );
      },
      width: "120px",
    },
    { name: "Group Name", selector: (row: User) => row.groupName },
    { name: "User Type", selector: (row: User) => row.userTypeName },
    {
      name: "User Name",
      selector: (row: User) => row.loginUserName,
      sortable: true,
    },
    {
      name: "",
      cell: (row: User) => (
        <div className="action-cell">
          <button
            className="action-button edit-button"
            title="Edit User"
            onClick={() => handleEdit(row.userId, row.tenantId)}
          >
            <i className="bi bi-pencil-square"></i>
          </button>
          <button
            className="action-button more-button"
            title="Change Password"
            onClick={() => handleOpenPasswordModal(row.userId)}
          >
            <i className="bi bi-gear"></i>
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "60px",
    },
  ];

  if (error) {
    return <div className="p-4 text-red-500">Error: {error}</div>;
  }

  return (
    <div className="wrapper">
      <div>
        <Toaster position="top-center" reverseOrder={false} />
      </div>
      {!isSidebarCollapsed && <CollapsibleSidebar />}
      <div className="main">
        <Navbar onToggleSidebar={toggleSidebar} />
        <div className="main-content-div">
          <div className="main-header-div"></div>
          <main className="content mainContent">
            <div className="container-fluid p-0">
              <div className="row">
                <div className="col-12 col-lg-12">
                  <div className="card">
                    <div className="card-header carHeaderWithBtn">
                      <div className="card-title mb-0">Users List</div>
                      <div>
                        <input
                          type="text"
                          placeholder="Search by Name, Email, or Mobile"
                          className="p-2 w-full datatableSearchInput"
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                        />
                        <button
                          className="demo-btn btn btn-outline-primary btn-sm"
                          onClick={() => setIsModalOpen(true)}
                        >
                          +Add User
                        </button>
                      </div>
                    </div>
                    <div className="card-body p-0">
                      <DataTable
                        columns={columns}
                        data={filteredUsers}
                        pagination
                        paginationPerPage={10}
                        highlightOnHover
                        striped
                        responsive
                        //noDataComponent="No users found"
                        selectableRows
                        selectableRowsComponentProps={{
                          className: "custom-checkbox",
                        }}
                        onSelectedRowsChange={handleRowSelected}
                        clearSelectedRows={toggleCleared}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <CreateContactModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              onSubmit={handleCreateContact}
              onUserDataAdd={fetchUsers}
            />
            <EditUserViewDemo
              isOpen={isEditModalOpen}
              onClose={() => {
                setIsEditModalOpen(false);
                setEditUserData(null);
              }}
              onSubmit={handleEditSubmit}
              selectedRows={selectedRows}
              editUserData={editUserData}
              onUserDataUpdated={fetchUsers}
            />
            {selectedUserId !== null && (
              <UserPasswordModal
                isOpen={isPasswordModalOpen}
                onClose={handleClosePasswordModal}
                userId={selectedUserId}
              />
            )}
          </main>
        </div>
        <Footer />
      </div>
      <OverlayLoader
        loading={loading}
        message="Loading User Data"
        submessage="This might take a moment..."
      />
    </div>
  );
};

export default UserTable;
